import {
  DagStatus,
  TaskAction,
  TerminationMode,
  isRunningStatus,
} from '@agilelab/plugin-wb-builder-common';
import {
  createActionsColumn,
  TableCellProps,
  WbButton,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { Box, Typography, useTheme } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { FC, useEffect } from 'react';
import { useReleaseDetailPageContext } from '../../context/useReleaseDetailPageContext';
import { useStepIcons } from '../../hooks/useStepIcons';
import { Deploy } from '../../types';
import { DeployProcess } from './DeployProcess';
import { ForceTerminateIcon } from './Icons';

import { LatestProvisioningActions } from './LatestProvisionigActions';

interface Props {
  deploy: Deploy;
  canProvision: boolean;
}

export const LatestProvisioningDetail: FC<Props> = ({
  deploy,
  canProvision,
}) => {
  const stepIcons = useStepIcons();
  const {
    isDeployingAll,
    isUndeployingAll,
    setIsDeployingAll,
    setIsUndeployingAll,
    terminateMode,
    terminate,
  } = useReleaseDetailPageContext();
  const theme = useTheme();

  const hide =
    isDeployingAll ||
    isUndeployingAll ||
    deploy.status !== DagStatus.TERMINATED;

  const getAction = (action: string) => {
    switch (action) {
      case 'PROVISION':
        return 'Deploy';
      case 'UNPROVISION':
        return 'Undeploy';
      default:
        return '';
    }
  };

  const cols: TableCellProps<Deploy>[] = [
    {
      headerName: 'Latest Action',
      field: 'action',
      fieldRender: row => (
        <Typography style={{ fontWeight: 700 }}>
          {getAction(row.action)}
        </Typography>
      ),
    },
    {
      headerName: 'Date',
      field: 'deployDate',
      fieldRender: row => (
        <Typography>
          {format(parseISO(row.deployDate), 'yyyy/MM/dd HH:mm:ss')}
        </Typography>
      ),
    },
    {
      headerName: 'Status',
      field: 'status',
      fieldRender: row => (
        <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
          {stepIcons[deploy.status]}
          <Typography style={{ fontWeight: 700 }}>
            {snakeCaseToTitleCase(row.status.toLowerCase())}
          </Typography>
        </Box>
      ),
    },
    {
      headerName: 'Process',
      field: 'process' as keyof Deploy,
      cellProps: { width: '40%' },
      fieldRender: row => <DeployProcess deploy={row} />,
    },
    createActionsColumn({
      width: 240,
      renderActions: row => (
        <LatestProvisioningActions deploy={row} canProvision={canProvision} />
      ),
    }),
  ];

  useEffect(() => {
    if (isRunningStatus(deploy.status)) {
      if (deploy.action === TaskAction.PROVISION) {
        setIsDeployingAll(true);
      }
      if (deploy.action === TaskAction.UNPROVISION) {
        setIsUndeployingAll(true);
      }
    }
  }, [deploy, setIsDeployingAll, setIsUndeployingAll]);

  return (
    <>
      <WbTable<Deploy>
        stickyHeader
        components={{
          tableContent: {
            columns: cols,
            rows: [deploy],
          },
        }}
      />
      {terminateMode === TerminationMode.SAFE &&
        deploy.status === DagStatus.RUNNING && (
          <Box
            padding={1}
            margin={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              backgroundColor: theme.palette.background.paper,
              gap: 16,
              marginTop: 16,
            }}
          >
            <Typography style={{ fontWeight: 600 }}>
              Process termination requestes.
            </Typography>
            <Typography>
              The operation may take some time, please wait. If the wait lasts
              longer than expected, click
            </Typography>
            <WbButton
              color="primary"
              size="small"
              icon={<ForceTerminateIcon />}
              onClick={_ => {
                terminate(TerminationMode.FORCED);
              }}
              variant="contained"
              style={{
                backgroundColor: theme.palette.error.main,
              }}
              label="Force Terminate"
            />
          </Box>
        )}

      {!hide && (
        <Box
          padding={1}
          margin={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            backgroundColor: theme.palette.background.paper,
            gap: 16,
            marginTop: 16,
          }}
        >
          <Typography style={{ fontWeight: 600 }}>
            The process has been terminated.
          </Typography>
        </Box>
      )}
    </>
  );
};
